import { useState, useEffect, useContext } from "react";
import {
  Album,
  EventBreadcrumbType,
  FolderBreadcrumbType,
  AlbumBreadcrumbType
} from "./types";
import { PhotoManagerContext } from ".";
import {
  decodeEntityPath,
  getEntityIdFromPath,
  getEntityInfoFromPath,
  getEntityTypeFromPath
} from "./FolderFeatures/EncryptionOfFolder";

export const useEventData = (
  entityType: string | null,
  entityId: string | null
) => {
  const [selectedEvent, setSelectedEvent] =
    useState<EventBreadcrumbType | null>(null);
  const [loadingEvent, setLoadingEvent] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);
  const fetchEventData = async () => {
    if (!entityType || !entityId) return;

    try {
      setLoadingEvent(true);
      const paramName =
        entityType === "LibraryFolderReference" ? "referenceId" : "id";

      const response = await fetch(
        `/api/v1/photo-manager/details?t=${entityType}&${paramName}=${entityId}&p=${context?.encodedString}`
      );
      if (!response.ok) throw new Error("Failed to fetch event data");
      const data = await response.json();

      setSelectedEvent(data.entities);
    } catch (error) {
      console.error("Error fetching event details:", error);
    } finally {
      setLoadingEvent(false);
    }
  };

  useEffect(() => {
    if (!entityType || !entityId) return;
    // Check if we already have the event data in context to avoid duplicate API calls
    // This happens when handleURLChange has already fetched the event details
    if (
      context?.selectedEvent &&
      context.selectedEvent.id === Number(entityId) &&
      (entityType === "Gallery" || entityType === "LibraryFolderReference")
    ) {
      setSelectedEvent(context.selectedEvent);
      return;
    }

    fetchEventData();
  }, [entityType, entityId, context?.selectedEvent, context?.selectedView]);

  return { selectedEvent, loadingEvent, fetchEventData };
};

export const useAlbumData = (
  albumId: string | null,
  referenceId: string | null
) => {
  const [selectedAlbum, setSelectedAlbum] =
    useState<AlbumBreadcrumbType | null>(null);
  const [loadingAlbum, setLoadingAlbum] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);

  const fetchAlbumData = async () => {
    if (!albumId) return;
    try {
      setLoadingAlbum(true);

      const paramName = "id";

      const response = await fetch(
        `/api/v1/photo-manager/details?t=Album&${paramName}=${albumId}&p=${context?.encodedString}`
      );

      if (!response.ok) throw new Error("Failed to fetch album data");

      const data = await response.json();
      const album = data.entities;
      
      // Only copy from context if the value is truly missing (undefined/null), not if it's explicitly false
      if (album.mobileCameraOnlyUpload == null && context?.selectedAlbum?.id === Number(albumId)) {
        album.mobileCameraOnlyUpload = context.selectedAlbum.mobileCameraOnlyUpload;
      }
      
      setSelectedAlbum(album);
    } catch (error) {
      console.error("Error fetching album details:", error);
    } finally {
      setLoadingAlbum(false);
    }
  };

  useEffect(() => {
    if (
      context?.selectedAlbum &&
      context.selectedAlbum.id === Number(albumId)
    ) {
      setSelectedAlbum(context.selectedAlbum);
      return;
    }

    fetchAlbumData();
  }, [albumId, referenceId, context?.encodedString, context?.selectedAlbum]);

  return { selectedAlbum, loadingAlbum, fetchAlbumData };
};

export const useFolderData = (
  folderId: number | null,
  referenceId: number | null
) => {
  const [activeFolder, setactiveFolder] = useState<FolderBreadcrumbType | null>(
    null
  );
  const [isLoadingFolder, setLoadingFolder] = useState<boolean>(false);
  const context = useContext(PhotoManagerContext);

  const fetchFolderData = async (
    folderId: number | null,
    referenceId: number | null
  ) => {
    try {
      setLoadingFolder(true);
      const response = await fetch(
        `/api/v1/photo-manager/details/?t=LibraryFolder&${
          referenceId ? "referenceId=" : "id="
        }${referenceId || folderId}&p=${context?.encodedString}`
      );
      if (!response.ok) throw new Error("Failed to fetch folder data");
      const data = await response.json();
      setactiveFolder(data.entities);
    } catch (error) {
      console.error("Error fetching folder details:", error);
    } finally {
      setLoadingFolder(false);
    }
  };

  useEffect(() => {
    if (referenceId || folderId) {
      fetchFolderData(folderId, referenceId);
    } else {
      setactiveFolder(null);
    }
  }, [folderId, referenceId]);

  return { activeFolder, isLoadingFolder, fetchFolderData };
};

export const useModalStates = () => {
  const [activeShareModalEventId, setActiveShareModalEventId] = useState<
    number | null
  >(null);
  const [markUploadPopupId, setMarkUploadPopupId] = useState<number | null>(
    null
  );
  const [privacyPopupId, setPrivacyPopupId] = useState<number | null>(null);
  const [showNewAlbumModal, setShowNewAlbumModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showSelfieSearchPreferencePopup, setShowSelfieSearchPreferencePopup] =
    useState(false);

  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);

  return {
    activeShareModalEventId,
    setActiveShareModalEventId,
    markUploadPopupId,
    setMarkUploadPopupId,
    privacyPopupId,
    setPrivacyPopupId,
    showNewAlbumModal,
    setShowNewAlbumModal,
    showDeleteModal,
    setShowDeleteModal,
    showSelfieSearchPreferencePopup,
    setShowSelfieSearchPreferencePopup,
    showSubscriptionPopup,
    setShowSubscriptionPopup
  };
};

export const getUrlParams = () => {
  const url = new URL(window.location.href);
  const encodedPath = url.searchParams.get("p");

  // Initialize return values
  let eventId: number | null = null;
  let albumId: number | null = null;
  let folderId: number | null = null;
  let referenceId: number | null = null;

  if (encodedPath) {
    const decodedPath = decodeEntityPath(encodedPath);

    if (decodedPath) {
      const { type, id } = getEntityInfoFromPath(decodedPath);

      // Assign to appropriate variable
      if (type === "gallery") eventId = id;
      else if (type === "album") albumId = id;
      else if (type === "folder") folderId = id;
      else if (type === "reference") referenceId = id;
      else console.warn("Unknown entity type:", type);
    }
  }

  return { eventId, albumId, folderId, referenceId };
};
